import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../Services/AuthContext";
import { getLegalMeta } from "../Services/AuthService";
import "../Styles/Register.css";
import "../Styles/Forms.css";
import { Field, FormError } from "../Components/Form/Field";
import { PasswordInput } from "../Components/Form/PasswordInput";
import { SubmitButton } from "../Components/Form/SubmitButton";
import { useSubmit } from "../Hooks/useSubmit";
import Footer from "../Components/Footer";
import { pendingInviteRoute } from "../Services/pendingInvite";

const MIN_AGE = 18;

/**
 * Senaste födelsedatum som ger 18 år i dag. Sätts som max på datumfältet,
 * så webbläsarens egen datumväljare inte ens erbjuder ett för sent datum.
 * Servern räknar om samma sak — det här är bekvämlighet, inte kontroll.
 */
function latestAllowedBirthDate(today = new Date()): string {
  const limit = new Date(
    Date.UTC(today.getUTCFullYear() - MIN_AGE, today.getUTCMonth(), today.getUTCDate())
  );

  return limit.toISOString().slice(0, 10);
}

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);

  /**
   * Villkorsversionen kommer från servern, inte från en konstant här.
   *
   * Servern kräver exakt den version som gäller och avvisar en tom. Går
   * /legal inte att hämta ska formuläret därför inte gå att skicka alls —
   * annars registreras användaren som om hen godkänt en text som aldrig
   * visades. legalState nedan styr det.
   */
  const [termsVersion, setTermsVersion] = useState("");
  const [legalState, setLegalState] = useState<"loading" | "ready" | "failed">("loading");

  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    getLegalMeta()
      .then((meta) => {
        if (cancelled) return;

        setTermsVersion(meta.termsVersion);
        setLegalState("ready");
      })
      .catch(() => {
        // Ingen gissad version. Formuläret spärras i stället, se nedan.
        if (!cancelled) setLegalState("failed");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const { submit, pending, error, fieldError, formError, clearError } = useSubmit(
    async () => {
      await register({
        firstName,
        lastName,
        email,
        password,
        birthDate,
        acceptTerms,
        termsVersion,
      });
    }
  );

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // Utan en bekräftad villkorsversion skickas ingenting. Servern skulle
    // ändå avvisa, men användaren ska inte behöva fylla i hela formuläret
    // för att få veta det.
    if (legalState !== "ready") return;

    const result = await submit();
    if (!result.ok) return;

    // Väntande vänbjudan går före onboardingen. Den som klickat sig hit
    // från en väns länk har redan sagt vad hen kom för att göra.
    const invite = pendingInviteRoute();
    navigate(invite ?? "/onboarding/artists", { replace: true });
  };

  const maxBirthDate = latestAllowedBirthDate();

  const onEdit = () => {
    if (error) clearError();
  };

  const termsError = fieldError("acceptTerms");

  return (
    <div className="register-container">
      <h2 className="hero-h2">Create an account to explore live music in real time.</h2>

      <form className="login-form" onSubmit={handleRegister} noValidate>
        <Field label="First name" error={fieldError("firstName")}>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                onEdit();
              }}
              autoComplete="given-name"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Last name" error={fieldError("lastName")}>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                onEdit();
              }}
              autoComplete="family-name"
              disabled={pending}
            />
          )}
        </Field>

        <Field label="Email" error={fieldError("email")} required>
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                onEdit();
              }}
              autoComplete="email"
              disabled={pending}
            />
          )}
        </Field>

        <Field
          label="Password"
          error={fieldError("password")}
          hint="At least 8 characters, with a letter and a number."
          required
        >
          {(props) => (
            <PasswordInput
              {...props}
              value={password}
              onChange={(value) => {
                setPassword(value);
                onEdit();
              }}
              autoComplete="new-password"
              disabled={pending}
            />
          )}
        </Field>

        <Field
          label="Date of birth"
          error={fieldError("birthDate")}
          hint="You need to be at least 18. We only store the year."
          required
        >
          {(props) => (
            <input
              {...props}
              className="form-input"
              type="date"
              value={birthDate}
              onChange={(e) => {
                setBirthDate(e.target.value);
                onEdit();
              }}
              min="1900-01-01"
              max={maxBirthDate}
              autoComplete="bday"
              disabled={pending}
            />
          )}
        </Field>

        {/*
          Inte Field: kryssrutans label ÄR texten med länkarna i, och en
          separat label ovanför hade gett fältet två namn och ett tomt.
        */}
        <div className="form-field">
          <label className="form-checkbox" htmlFor="accept-terms">
            <input
              id="accept-terms"
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => {
                setAcceptTerms(e.target.checked);
                onEdit();
              }}
              disabled={pending || legalState !== "ready"}
              required
              aria-invalid={termsError ? true : undefined}
              aria-describedby={termsError ? "accept-terms-error" : undefined}
            />
            <span>
              I accept the{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">
                Terms of Service
              </Link>{" "}
              and the{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </Link>
              .
            </span>
          </label>

          {termsError && (
            <p className="form-error" id="accept-terms-error" role="alert">
              {termsError}
            </p>
          )}
        </div>

        <FormError message={formError} />

        {legalState === "failed" && (
          <p className="form-error" role="alert">
            We could not load the current terms, so we cannot record what you
            agreed to. Reload the page and try again.
          </p>
        )}

        <SubmitButton
          pending={pending}
          disabled={legalState !== "ready"}
          pendingLabel="Creating account…"
        >
          Sign up
        </SubmitButton>

        <Link className="form-link" to="/login">
          Sign in instead
        </Link>
      </form>

      <Footer />
    </div>
  );
}
